import { useState } from "react";
import { Link } from "react-router-dom";
import { PlaneTakeoff, PlaneLanding, Search, MapPin, Armchair, UtensilsCrossed, Map } from "lucide-react";
import api from "../lib/api";
import { GoogleFlightsCard } from "../components/GoogleFlightsCard";
import { AppTopBar } from "../components/AppScreen";

/* Flight Smart Search: enter a flight number, see terminal/gate/status plus
   deep links into our own airport guides (lounges, food & shops, maps). */

const MovementCard = ({ title, icon, m }) => {
  if (!m) return null;
  return (
    <div className="border border-zinc-300 bg-white p-5 flex-1 min-w-[260px]">
      <div className="flex items-center gap-2 font-heading font-900 text-sm uppercase tracking-widest text-violet-700 mb-3">{icon}{title}</div>
      <div className="text-2xl font-heading font-900">{m.iata || "—"}</div>
      <div className="text-sm text-muted-foreground mb-3">{m.airport_name || ""}</div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div><span className="text-muted-foreground">Terminal</span><div className="font-700" data-testid={`${title.toLowerCase()}-terminal`}>{m.terminal || "—"}</div></div>
        <div><span className="text-muted-foreground">Gate</span><div className="font-700" data-testid={`${title.toLowerCase()}-gate`}>{m.gate || "—"}</div></div>
        <div><span className="text-muted-foreground">Scheduled</span><div className="font-700">{(m.scheduled_local || "—").replace(/(\d{2}:\d{2}).*/, "$1").slice(0, 16)}</div></div>
        <div><span className="text-muted-foreground">Estimated</span><div className="font-700">{(m.estimated_local || "—").replace(/(\d{2}:\d{2}).*/, "$1").slice(0, 16)}</div></div>
      </div>
      {m.links && (
        <div className="flex flex-wrap gap-2 mt-4">
          <Link to={m.links.guide} className="inline-flex items-center gap-1 text-xs font-700 border border-violet-300 text-violet-700 px-2.5 py-1.5 hover:bg-violet-50" data-testid={`${title.toLowerCase()}-link-guide`}><MapPin className="w-3.5 h-3.5" /> Airport guide</Link>
          <Link to={m.links.lounges} className="inline-flex items-center gap-1 text-xs font-700 border border-violet-300 text-violet-700 px-2.5 py-1.5 hover:bg-violet-50" data-testid={`${title.toLowerCase()}-link-lounges`}><Armchair className="w-3.5 h-3.5" /> Lounges</Link>
          <Link to={m.links.food_shops} className="inline-flex items-center gap-1 text-xs font-700 border border-violet-300 text-violet-700 px-2.5 py-1.5 hover:bg-violet-50" data-testid={`${title.toLowerCase()}-link-food`}><UtensilsCrossed className="w-3.5 h-3.5" /> Food & shops</Link>
          <Link to={m.links.maps} className="inline-flex items-center gap-1 text-xs font-700 border border-violet-300 text-violet-700 px-2.5 py-1.5 hover:bg-violet-50" data-testid={`${title.toLowerCase()}-link-maps`}><Map className="w-3.5 h-3.5" /> Maps</Link>
        </div>
      )}
    </div>
  );
};

export default function FlightSearch() {
  const [num, setNum] = useState("");
  const [flights, setFlights] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    if (!num.trim() || busy) return;
    setBusy(true); setError(""); setFlights(null);
    try {
      const { data } = await api.get(`/flightsearch/${encodeURIComponent(num.trim())}`);
      setFlights(data.flights);
      if (!data.flights.length) setError("No flight found for that number on the nearest operating day.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Lookup failed — try again");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="flight-search-page">
      <AppTopBar title="Flights" />
      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-12">
      <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight flex items-center gap-3">
        <PlaneTakeoff className="w-10 h-10 text-violet-700" /> Flight Smart Search
      </h1>
      <p className="text-muted-foreground mt-3 mb-8 max-w-2xl">
        Enter your flight number to instantly see your terminal, gate and status — with one-tap
        access to the lounges, food and maps at both airports.
      </p>

      <form onSubmit={search} className="flex gap-2 mb-8">
        <input value={num} onChange={(e) => setNum(e.target.value)} placeholder="e.g. AS123 or EK 230"
          data-testid="flight-search-input"
          className="flex-1 border-2 border-zinc-300 px-4 py-4 text-base outline-none focus:border-violet-600 transition-colors uppercase" />
        <button type="submit" disabled={busy} data-testid="flight-search-btn"
          className="inline-flex items-center gap-2 bg-violet-700 text-white px-6 font-700 hover:bg-violet-600 disabled:opacity-50">
          <Search className="w-4 h-4" /> {busy ? "Searching…" : "Search"}
        </button>
      </form>

      {error && <p className="text-sm text-rose-600 font-600 mb-6" data-testid="flight-search-error">{error}</p>}

      <GoogleFlightsCard />

      {flights?.map((f, i) => (
        <div key={i} className="mb-8" data-testid={`flight-result-${i}`}>
          <div className="flex items-center gap-3 mb-3">
            <span className="font-heading font-900 text-xl">{f.number}</span>
            {f.airline && <span className="text-sm text-muted-foreground">{f.airline}</span>}
            {f.status && <span className="text-[11px] font-900 tracking-widest bg-violet-100 text-violet-700 px-2 py-0.5" data-testid={`flight-status-${i}`}>{f.status.toUpperCase()}</span>}
          </div>
          <div className="flex flex-wrap gap-4">
            <MovementCard title="Departure" icon={<PlaneTakeoff className="w-4 h-4" />} m={f.departure} />
            <MovementCard title="Arrival" icon={<PlaneLanding className="w-4 h-4" />} m={f.arrival} />
          </div>
        </div>
      ))}

      <p className="text-xs text-muted-foreground mt-6">
        Gate and terminal data may be partial for some airlines and airports — always confirm with
        your airline and airport displays.
      </p>
      </div>
    </div>
  );
}
